import React, { useEffect, useRef } from 'react';
import './ChatHistory.css';

const ChatHistory = ({ messages, isLoading, activeTab }) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const renderMessageText = (text) => {
    // Split by newlines and create proper line breaks
    const lines = text.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const isUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const renderDocuments = (documents) => {
    if (!documents || documents.length === 0) {
      return null;
    }

    // Function to remove file extension from document name
    const removeFileExtension = (fileName) => {
      if (!fileName) return fileName;
      
      let cleanName = fileName;
      
      // Common file extensions to remove
      const extensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.rtf', '.odt', '.ods', '.odp'];
      
      for (const ext of extensions) {
        if (cleanName.toLowerCase().endsWith(ext.toLowerCase())) {
          cleanName = cleanName.slice(0, -ext.length);
          break;
        }
      }
      
      // Remove specific words (case-insensitive)
      const wordsToRemove = ['final candidate', 'final', 'draft', 'formatted', 'unformatted'];
      
      for (const word of wordsToRemove) {
        // Use regex with word boundaries to avoid partial matches
        // Also handle various spacing and positioning
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        cleanName = cleanName.replace(regex, '');
      }
      
      // Clean up extra spaces and trim
      cleanName = cleanName.replace(/\s+/g, ' ').trim();
      
      return cleanName;
    };

    return (
      <div className="document-sources">
        <div className="sources-label">Sources:</div>
        <ul className="sources-list">
          {documents.map((doc, index) => {
            const cleanFileName = removeFileExtension(doc.file_name);
            
            if (isUrl(doc.file_id) && doc.file_name) {
              // Display as a link if file_id is URL and file_name exists
              return (
                <li key={index}>
                  <a href={doc.file_id} target="_blank" rel="noopener noreferrer">
                    {cleanFileName}
                  </a> {doc.age && (<i>({doc.age})</i>)}
                </li>
              );
            } else if (doc.file_name) {
              // Display as white paper if file_id is not URL
              return (
                <li key={index} class="fade-right">
                  {cleanFileName}
                </li>
              );
            }
            return null;
          })}
        </ul>
      </div>
    );
  };

  const getAuthorImage = (authorId) => {
    const imageMap = {
      JE: `${process.env.PUBLIC_URL}/images/JE.jpg`,
      JB: `${process.env.PUBLIC_URL}/images/JB.jpg`,
      EN: `${process.env.PUBLIC_URL}/images/EN.jpg`,
      General: `${process.env.PUBLIC_URL}/images/intellyx-logo.png`
    };
    return imageMap[authorId] || '';
  };

  const renderMultipleResponses = (multipleResponses) => {
    // Filter responses that start with "Sorry, "
    const validResponses = multipleResponses.filter(response => 
      !response.text.trim().startsWith('Sorry, ')
    );
    
    // If all responses start with "Sorry, ", display special message
    if (validResponses.length === 0) {
      return (
        <div className="no-information-message">
          <p>There is not enough information in Intellyx knowledge base on this subject.</p>
        </div>
      );
    }
    
    // Separate General responses from regular author responses
    const regularResponses = validResponses.filter(response => response.author !== 'General');
    const generalResponses = validResponses.filter(response => response.author === 'General');
    
    // Display regular responses first, then General responses last
    const orderedResponses = [...regularResponses, ...generalResponses];
    
    return (
      <div className="multiple-responses">
        {orderedResponses.map((response, index) => (
          <div key={index} className="author-response">
            <div className="author-label">
              <div 
                className="author-image"
                style={{
                  backgroundImage: `url(${getAuthorImage(response.author)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: response.author === 'General' ? 'left center' : 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              ></div>
              {response.author !== 'General' && `${response.author}:`}
              {response.author === 'General' && ':'}
            </div>
            <div className="author-response-content">
              <p>{renderMessageText(response.text)}</p>
              {/* Don't show documents for responses that start with "Sorry, " */}
              {!response.text.trim().startsWith('Sorry, ') && renderDocuments(response.documents)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="chat-history">
      {messages.length === 0 && !isLoading && (
        <div className="welcome-message">
          <p>Welcome! Start a conversation by typing a message below.</p>
        </div>
      )}
      
      {messages.map((message) => (
        <div
          key={message.id}
          className={`message ${message.sender} ${message.isError ? 'error' : ''}`}
        >
          <div className="message-content">
            {message.multipleResponses ? (
              renderMultipleResponses(message.multipleResponses)
            ) : (
              <>
                {message.sender === 'bot' && activeTab && activeTab !== 'all' && (
                  <div className="author-label">
                    <div 
                      className="author-image"
                      style={{
                        backgroundImage: `url(${getAuthorImage(activeTab)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    ></div>
                    {activeTab}:
                  </div>
                )}
                <p>{renderMessageText(message.text)}</p>
                {/* Don't show documents for bot responses that start with "Sorry, " */}
                {message.sender === 'bot' && 
                 !message.text?.trim().startsWith('Sorry, ') && 
                 renderDocuments(message.documents)}
              </>
            )}
          </div>
          <div className="message-time">
            {formatTime(message.timestamp)}
          </div>
        </div>
      ))}
      
      {isLoading && (
        <div className="message bot loading">
          <div className="message-content">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
  };

export default ChatHistory; 